import { PhysiologyFields } from "../../modals/PhysiologyFields";
import { useRovingRadio } from "../../../hooks/useRovingRadio";
import { SWEAT_QUESTION } from "../../../lib/questionCopy";
import type { AboutYouFuel } from "./useAboutYouFuel";

/**
 * "About your body" (wizard step "body", D-16) — sex/weight + the sweat question,
 * split from the old combined About-you step (allergens + flavors moved to the
 * Needs & tastes step). Never blocks Continue.
 *
 * D-11: sweat starts UNSELECTED (`fuel.sweat === null`) — nothing renders
 * picked until the athlete chooses or the server profile hydrates a real
 * value. "Not sure" stays a pickable choice.
 *
 * Renders the inner `.fgp-wz-fuel` block only — the caller owns the chrome.
 */
export function BodyStep({ fuel }: { fuel: AboutYouFuel }) {
  const sweatIndex = SWEAT_QUESTION.options.findIndex((o) => o.value === fuel.sweat);
  const pickSweat = (value: (typeof SWEAT_QUESTION.options)[number]["value"]) => {
    fuel.sweatTouchedRef.current = true;
    fuel.setSweat(value);
  };
  const sweatRove = useRovingRadio({
    length: SWEAT_QUESTION.options.length,
    selectedIndex: sweatIndex,
    onArrowSelect: (n) => pickSweat(SWEAT_QUESTION.options[n]!.value)
  });

  return (
    <div className="fgp-wz-fuel fgp-wz-fuel--about">
      <PhysiologyFields
        sex={fuel.sex}
        weight={fuel.weightText}
        weightUnit={fuel.weightUnit}
        onSexChange={(s) => {
          fuel.physiologyTouchedRef.current = true;
          fuel.setSex(s);
        }}
        onWeightChange={(w) => {
          fuel.physiologyTouchedRef.current = true;
          fuel.setWeightText(w);
        }}
        onWeightUnitChange={(u) => {
          fuel.physiologyTouchedRef.current = true;
          fuel.setWeightUnit(u);
        }}
        variant="wizard-split"
      />
      {/* ── Sweat / sodium — ONE column at all widths (D-12) ───────────── */}
      <div className="fgp-wz-field">
        <div id="fgp-wz-sweat-label" className="fgp-wz-fuel__label">
          {SWEAT_QUESTION.label}
        </div>
        <div
          className="fgp-wz-choices"
          style={{ flexDirection: "column" }}
          role="radiogroup"
          aria-labelledby="fgp-wz-sweat-label">
          {SWEAT_QUESTION.options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={fuel.sweat === o.value}
              tabIndex={sweatRove.tabIndexFor(i)}
              onKeyDown={(e) => sweatRove.onKeyDown(e, i)}
              className={`fgp-wz-choice${fuel.sweat === o.value ? " fgp-wz-choice--on" : ""}`}
              onClick={() => pickSweat(o.value)}>
              <span className="fgp-wz-choice__t">{o.title}</span>
              <span className="fgp-wz-choice__s">{o.desc}</span>
            </button>
          ))}
        </div>
        {fuel.hasExplicitSodium ? (
          <div className="fgp-wz-sodium">
            You&apos;ve set an exact target of{" "}
            <strong>{fuel.profileView?.profile?.sodiumTarget} mg sodium/hr</strong> — pick a
            sweat level only if you want to replace it.
          </div>
        ) : null}
      </div>
    </div>
  );
}
